import { State, useHookstate } from '@hookstate/core'
import { PDFViewer } from '@react-pdf/renderer'
import { Button, Checkbox, Col, Drawer, Row } from 'antd'
import { useEffect } from 'react'
import { CustomView } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Icons from '../../../../../../../Main/Components/Icons'
import MultiSelect from '../../../../../../../Main/Components/MultiSelect'
import { ITEM_STATES, JobItem } from '../../../../domain/items'
import { ItemPdf } from './item-pdf.component'
const CheckboxGroup = Checkbox.Group

const ItemCategorySection = styled.div`
  & > label:nth-child(1) {
    color: #00000073;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 400;
    display: block;
    margin-bottom: 1.4rem;
  }
  & > label:nth-child(2) {
    color: #000000d9;
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 600;
    display: block;
    margin-bottom: 0.8rem;
    & > span {
      vertical-align: middle;
    }
  }
  & > div {
    margin-bottom: 1.6rem;
  }
`

const ItemStatusSection = styled.div`
  margin-bottom: 2.6rem;
  & > label:nth-child(1) {
    color: #000000d9;
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 600;
    display: block;
    margin-bottom: 0.8rem;
    & > span {
      vertical-align: middle;
    }
  }
`

type ItemCategoryOrState = {
  label: string
  value: string
}

type PackageType = {
  department: string
  id: string
  name: string
  reference: string
  state: string
  storeId: string
  transferState: string
  zone: string
}

type ZonePickingPackageCategory = {
  [key: string]: JobItem[]
}

type ZonePickingPackage = {
  package: PackageType
  categories: ZonePickingPackageCategory
}

type PrintItemDrawerProps = {
  showItemPrintDrawer: State<boolean>
  onShowOrHideItemPrintDrawer: (show: boolean) => void
  items: State<JobItem[]>
  clientLogoURL: string
  job: any
}

const PrintItemDrawer = (props: PrintItemDrawerProps) => {
  const { showItemPrintDrawer, onShowOrHideItemPrintDrawer, items, clientLogoURL, job } = props
  const { t } = useTranslation()
  const itemCategories = useHookstate<ItemCategoryOrState[]>([])
  const selectedItemCategories = useHookstate<string[]>([])
  const itemStates = useHookstate<ItemCategoryOrState[]>([])
  const selectedItemStates = useHookstate<string[]>([])
  const showPdfItems = useHookstate<boolean>(false)
  const zonePickingPackages = useHookstate<ZonePickingPackage[]>([])

  useEffect(() => {
    getItemCategories()
    getItemStates()
  }, [])

  const getItemCategories = (): void => {
    let categories: ItemCategoryOrState[] = []

    if (items.get().length) {
      categories = Array.from(
        new Set(
          items.get({ noproxy: true }).map((item) => {
            return item.attributes.category || t('jobs.job.info.items-view.print.no-category')
          })
        )
      ).map((name: string) => {
        return { label: name, value: name }
      })
    }

    itemCategories.set(categories)
  }

  const getItemStates = (): void => {
    let states: ItemCategoryOrState[] = []

    if (items.get().length) {
      states = Array.from(
        new Set(
          items.get({ noproxy: true }).map((item) => {
            return item.state === ITEM_STATES.REJECTED || item.state === ITEM_STATES.CANCELLED
              ? ITEM_STATES.REMOVED
              : item.state
          })
        )
      ).map((state: string) => {
        return { label: t<string>(`jobs.job.info.items-view.${state.toLocaleLowerCase()}`), value: state }
      })
    }

    itemStates.set(states)
  }

  const getMultiSelectText = (): string => {
    if (selectedItemCategories.get().length === 0) {
      return t<string>('jobs.job.info.items-view.print.multiSelect.buttonText.nothing')
    } else if (itemCategories.get().length === selectedItemCategories.get().length) {
      return t<string>('jobs.job.info.items-view.print.multiSelect.buttonText.all')
    } else {
      return t<string>('jobs.job.info.items-view.print.multiSelect.buttonText.some', {
        quantity: `${selectedItemCategories.get().length}/${itemCategories.get().length}`
      })
    }
  }

  const onChangeItemCategory = (categories: string[]): void => {
    selectedItemCategories.set(categories)
  }

  const onChangeItemStates = (states: string[]): void => {
    selectedItemStates.set(states)
  }

  const onPrintItemsPdf = (): void => {
    zonePickingPackages.set(createPackagesMap())
    showPdfItems.set(true)
  }

  const createCategoriesMap = (items: JobItem[]): ZonePickingPackageCategory => {
    const newCategoriesMap: ZonePickingPackageCategory = {}
    if (items && items.length) {
      items.map((item) => {
        let {
          attributes: { category }
        } = item
        category = category || t<string>('jobs.job.info.items-view.print.no-category')

        if (
          (selectedItemCategories.get({ noproxy: true }).length === 0 ||
            selectedItemCategories.get({ noproxy: true }).includes(category)) &&
          (selectedItemStates.get({ noproxy: true }).length === 0 ||
            selectedItemStates
              .get({ noproxy: true })
              .includes(
                item.state === ITEM_STATES.REJECTED || item.state === ITEM_STATES.CANCELLED
                  ? ITEM_STATES.REJECTED
                  : item.state
              ))
        ) {
          if (!newCategoriesMap[category]) {
            newCategoriesMap[category] = []
          }
          newCategoriesMap[category].push(item)
        }

        return category
      })
    }
    return newCategoriesMap
  }

  const createPackagesMap = (): ZonePickingPackage[] => {
    showPdfItems.set(false)
    let itemPackages: ZonePickingPackage[] = []

    if (items.get({ noproxy: true }).length) {
      if (job.packages && job.packages.length > 0) {
        itemPackages = [null, ...job.packages].map((itemPackage) => {
          return {
            package: itemPackage,
            categories: createCategoriesMap(
              JSON.parse(JSON.stringify(items.get())).filter(
                (item) =>
                  itemPackage === item.packageReference ||
                  (itemPackage && item.packageReference === itemPackage.reference)
              )
            )
          }
        })
      } else {
        itemPackages.push({
          package: null,
          categories: createCategoriesMap(JSON.parse(JSON.stringify(items.get())))
        })
      }
    }

    return itemPackages
  }

  const closeDrawerToPrintItems = (): void => {
    selectedItemCategories.set([])
    selectedItemStates.set([])
    showItemPrintDrawer.set(false)
    showPdfItems.set(false)
    onShowOrHideItemPrintDrawer(false)
  }

  return (
    <Drawer
      title={t<string>('jobs.job.info.items-view.print.title')}
      width={800}
      onClose={() => closeDrawerToPrintItems()}
      visible={showItemPrintDrawer.get()}
    >
      <Row>
        <Col span={24}>
          <ItemCategorySection>
            <label>{t<string>('jobs.job.info.items-view.print.text')}</label>

            <label>
              <span>
                <Icons iconName='Category' />
              </span>{' '}
              {t('jobs.job.info.items-view.print.categories-label')}
            </label>

            <MultiSelect
              buttonText={getMultiSelectText()}
              checkListTitle={t('jobs.job.info.items-view.print.multiSelect.categories')}
              searchText={t('jobs.job.info.items-view.print.multiSelect.searchText')}
              saveText={t('jobs.job.info.items-view.print.multiSelect.save')}
              onChangeSelected={onChangeItemCategory}
              dataSource={JSON.parse(JSON.stringify(itemCategories.get()))}
              dataSelected={JSON.parse(JSON.stringify(selectedItemCategories.get()))}
            />
          </ItemCategorySection>

          <ItemStatusSection>
            <label>
              <span>
                <Icons iconName='Loader' />
              </span>{' '}
              {t<string>('jobs.job.info.items-view.print.state-label')}
            </label>

            <CheckboxGroup
              options={JSON.parse(JSON.stringify(itemStates.get()))}
              value={JSON.parse(JSON.stringify(selectedItemStates.get()))}
              onChange={onChangeItemStates}
            />
          </ItemStatusSection>

          <Row>
            <Col span={24} style={{ textAlign: 'right', marginBottom: '2.2rem' }}>
              <Button
                type='primary'
                onClick={onPrintItemsPdf}
                disabled={selectedItemCategories.get().length === 0 || selectedItemStates.get().length === 0}
              >
                {t<string>('jobs.job.info.items-view.print.action')}
              </Button>
            </Col>
          </Row>

          {showPdfItems.get() && (
            <Row>
              <Col span={24}>
                <CustomView condition={true}>
                  <PDFViewer width='100%' height='600px'>
                    <ItemPdf
                      job={job}
                      items={items}
                      selectedItemCategories={selectedItemCategories}
                      selectedItemStates={selectedItemStates}
                      clientLogoURL={clientLogoURL}
                      categoriesSize={itemCategories.get().length}
                      zonePickingPackages={zonePickingPackages}
                    />
                  </PDFViewer>
                </CustomView>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Drawer>
  )
}

export { PrintItemDrawer }
